import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNewsArticles, getPortfolioItemsWithContent } from '../sanity/client';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './NewsList.css';

const NewsList = () => {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const { getText } = useCachedTexts('news');

  // Helper to get localized title
  const getLocalizedTitle = (item) => {
    const lang = i18n.language === 'it' ? 'It' : 'En';
    // Both articles and projects now have localized titles
    return item[`title${lang}`] || item.titleEn || item.title;
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [newsData, projectsData] = await Promise.all([
          getNewsArticles(),
          getPortfolioItemsWithContent()
        ]);
        setArticles(newsData || []);
        setProjects(projectsData || []);
      } catch (err) {
        console.error('Error fetching content:', err);
        setError('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Combine articles and projects, then filter and sort
  const filteredItems = useMemo(() => {
    // Mark articles and projects with their type
    const articlesWithType = articles.map(article => ({
      ...article,
      type: 'article',
      date: article.publishedDate
    }));
    
    const projectsWithType = projects.map(project => ({
      ...project,
      type: 'project',
      date: project.publishedDate // Projects now have publishedDate
    }));
    
    // Combine both
    let result = [...articlesWithType, ...projectsWithType];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => {
        const title = getLocalizedTitle(item);
        return title?.toLowerCase().includes(query);
      });
    }

    // Filter by date range (applies to both articles and projects)
    if (startDate) {
      const start = new Date(startDate);
      result = result.filter(item => {
        if (!item.date) return false;
        return new Date(item.date) >= start;
      });
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(item => {
        if (!item.date) return false;
        return new Date(item.date) <= end;
      });
    }

    // Sort: both articles and projects by date
    result.sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [articles, projects, searchQuery, startDate, endDate, sortOrder]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="news-list">
        <div className="news-filters">
          <div className="search-box skeleton-search"></div>
          <div className="filter-group">
            <div className="skeleton-filter"></div>
            <div className="skeleton-filter"></div>
          </div>
        </div>
        <div className="news-list-loading">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="news-item-skeleton">
              <div className="skeleton-date"></div>
              <div className="skeleton-title"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-list">
        <p className="news-list-error">{error}</p>
      </div>
    );
  }

  if (articles.length === 0 && projects.length === 0) {
    return (
      <div className="news-list">
        <p className="news-list-empty">{getText('empty_state', t('news.empty'))}</p>
      </div>
    );
  }

  return (
    <div className="news-list">
      {/* Search and Filters */}
      <div className="news-filters">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder={getText('search_placeholder', t('news.searchPlaceholder'))}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <div className="date-filter">
            <label>{t('news.from')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
            />
          </div>

          <div className="date-filter">
            <label>{t('news.to')}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </div>

          <div className="filter-select">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">{getText('sort_newest', t('news.newestFirst'))}</option>
              <option value="oldest">{getText('sort_oldest', t('news.oldestFirst'))}</option>
            </select>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="news-results-info">
        <span>{t(filteredItems.length === 1 ? 'news.articlesCount' : 'news.articlesCount_plural', { count: filteredItems.length })}</span>
        {(searchQuery || startDate || endDate) && (
          <button className="clear-filters" onClick={() => { setSearchQuery(''); setStartDate(''); setEndDate(''); }}>
            {getText('clear_filters', t('news.clearFilters'))}
          </button>
        )}
      </div>

      {/* Items list */}
      {filteredItems.length === 0 ? (
        <p className="news-list-empty">{getText('no_match', t('news.noMatch'))}</p>
      ) : (
        <ul className="news-list-items">
          {filteredItems.map((item) => (
            <li key={item._id} className={`news-list-item ${item.type === 'project' ? 'news-list-item-project' : ''}`}>
              <Link 
                to={item.type === 'article' ? `/news/${item.slug}` : `/portfolio/${item.slug}`} 
                className="news-list-link"
              >
                {item.type === 'project' && (
                  <span className="news-item-badge">{getText('featured_project', t('news.featuredProject'))}</span>
                )}
                <span className="news-item-date">
                  {formatDate(item.date)}
                </span>
                <span className="news-item-title">{getLocalizedTitle(item)}</span>
                <svg 
                  className="news-item-arrow" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NewsList;
